import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './RenouvellementTitreSejour.css';
import { 
  FaFileAlt, 
  FaClock, 
  FaCalendarCheck, 
  FaEnvelope, 
  FaPhone, 
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationTriangle,
  FaQuestionCircle
} from 'react-icons/fa';

const RenouvellementTitreSejour = () => {
  const navigate = useNavigate();

  const steps = [
    {
      icon: <FaClock />,
      title: "Anticipation",
      description: "Commencez les démarches 2-3 mois avant l'expiration de votre titre de séjour actuel"
    },
    {
      icon: <FaFileAlt />,
      title: "Préparation des documents",
      description: "Rassemblez tous les documents nécessaires (passeport, justificatifs, etc.)"
    },
    {
      icon: <FaEnvelope />,
      title: "Dépôt du dossier",
      description: "Déposez votre dossier en ligne sur le site de la préfecture"
    },
    {
      icon: <FaCalendarCheck />,
      title: "Suivi et réception",
      description: "Suivez l'avancement et recevez votre nouveau titre"
    }
  ];

  const documents = [
    "Passeport en cours de validité",
    "Titre de séjour actuel",
    "Justificatif de domicile",
    "Photos d'identité récentes",
    "Certificat de scolarité ou attestation de réussite",
    "Justificatifs de ressources (minimum légal)",
    "Attestation d'assurance maladie",
    "Visa long séjour valide"
  ];

  const timeline = [
    { date: "2-3 mois avant", action: "Début des démarches et prise de rendez-vous" },
    { date: "1-2 mois avant", action: "Préparation et constitution du dossier" },
    { date: "1 mois avant", action: "Dépôt du dossier en ligne" },
    { date: "Jour J", action: "Rendez-vous à la préfecture si nécessaire" },
    { date: "2-3 mois après", action: "Réception du récépissé" },
    { date: "3-6 mois après", action: "Réception du nouveau titre de séjour" }
  ];

  return (
    <div className="rts-page">
      {/* Hero Section */}
      <div className="rts-hero">
        <div className="rts-hero__background"></div>
        <div className="rts-hero__content">
          <button className="rts-back-button" onClick={() => navigate('/nos-services')}>
            <FaArrowLeft /> Retour aux services
          </button>
          <div className="rts-hero__icon">
            <FaFileAlt />
          </div>
          <h1 className="rts-hero__title">Renouvellement de titre de séjour</h1>
          <p className="rts-hero__subtitle">Accompagnement complet pour le renouvellement de votre titre de séjour étudiant</p>
        </div>
      </div>

      <div className="rts-content">
        <div className="rts-container">
          {/* Introduction */}
          <div className="rts-intro">
            <h2 className="rts-section-title">Pourquoi renouveler votre titre de séjour ?</h2>
            <p className="rts-intro__text">
              Le renouvellement de votre titre de séjour est une étape cruciale pour poursuivre vos études 
              en France. Un renouvellement dans les délais vous permet de rester en situation régulière et 
              de continuer à bénéficier de vos droits (travail, sécurité sociale, etc.). Notre service 
              vous accompagne pas à pas dans cette démarche administrative complexe.
            </p>
          </div>

          {/* Process Steps */}
          <div className="rts-section">
            <h2 className="rts-section-title">Les étapes du renouvellement</h2>
            <div className="rts-grid rts-grid--steps">
              {steps.map((step, index) => (
                <div className="rts-card rts-card--step" key={index}>
                  <div className="rts-card__step-number">{index + 1}</div>
                  <div className="rts-card__icon">{step.icon}</div>
                  <h3 className="rts-card__title">{step.title}</h3>
                  <p className="rts-card__description">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Documents Required */}
          <div className="rts-section">
            <h2 className="rts-section-title">Documents nécessaires</h2>
            <div className="rts-grid rts-grid--documents">
              {documents.map((doc, index) => (
                <div className="rts-doc-item" key={index}>
                  <FaCheckCircle className="rts-doc-item__check" />
                  <span>{doc}</span>
                </div>
              ))}
            </div>
            <div className="rts-doc-note">
              <FaExclamationTriangle className="rts-doc-note__icon" />
              <p>La liste peut varier selon les préfectures. Nous vous aidons à vérifier les documents spécifiques à votre situation.</p>
            </div>
          </div>

          {/* Timeline */}
          <div className="rts-section">
            <h2 className="rts-section-title">Calendrier indicatif</h2>
            <div className="rts-timeline">
              {timeline.map((item, index) => (
                <div className="rts-timeline__item" key={index}>
                  <div className="rts-timeline__date">{item.date}</div>
                  <div className="rts-timeline__dot"></div>
                  <div className="rts-timeline__content">
                    <p>{item.action}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="rts-section">
            <h2 className="rts-section-title">Questions fréquentes</h2>
            <div className="rts-faq-list">
              <div className="rts-faq-item">
                <FaQuestionCircle className="rts-faq-item__icon" />
                <div className="rts-faq-item__content">
                  <h3 className="rts-faq-item__title">Quand dois-je commencer les démarches ?</h3>
                  <p className="rts-faq-item__text">Idéalement 2 à 3 mois avant l'expiration de votre titre de séjour actuel pour éviter tout délai.</p>
                </div>
              </div>
              <div className="rts-faq-item">
                <FaQuestionCircle className="rts-faq-item__icon" />
                <div className="rts-faq-item__content">
                  <h3 className="rts-faq-item__title">Que faire si mon titre expire avant le rendez-vous ?</h3>
                  <p className="rts-faq-item__text">Vous recevrez un récépissé qui vous permet de rester en situation régulière pendant l'instruction.</p>
                </div>
              </div>
              <div className="rts-faq-item">
                <FaQuestionCircle className="rts-faq-item__icon" />
                <div className="rts-faq-item__content">
                  <h3 className="rts-faq-item__title">Combien coûte le renouvellement ?</h3>
                  <p className="rts-faq-item__text">Le timbre fiscal est de 225€ pour un titre de séjour étudiant (montant variable selon les années).</p>
                </div>
              </div>
              <div className="rts-faq-item">
                <FaQuestionCircle className="rts-faq-item__icon" />
                <div className="rts-faq-item__content">
                  <h3 className="rts-faq-item__title">Puis-je travailler pendant le renouvellement ?</h3>
                  <p className="rts-faq-item__text">Oui, avec le récépissé vous conservez les mêmes droits que votre titre actuel.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="rts-contact">
            <h2 className="rts-section-title rts-section-title--centered">Besoin d'aide ?</h2>
            <p className="rts-contact__text">Notre équipe est là pour vous accompagner dans toutes vos démarches</p>
            <div className="rts-contact-buttons">
              <Link to="/contact" className="rts-button rts-button--primary">
                <FaEnvelope /> Nous contacter
              </Link>
              <Link to="/rendez-vous" className="rts-button rts-button--secondary">
                <FaPhone /> Prendre rendez-vous
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenouvellementTitreSejour;